import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Search, Tag, Percent, Gift, Ticket, Calendar, Store, Package, FolderTree, Check, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateOnly, formatDateForInput } from '@/utils/dateFormat';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { offersAPI, storesAPI, productsAPI, categoriesAPI, brandsAPI } from '@/services/api';
import SearchableMultiSelect from '@/components/SearchableMultiSelect';
import ServerProductMultiSelect from '@/components/ServerProductMultiSelect';
import { OfferTiersEditor, emptyTier, formatTier } from '@/components/hq/OfferTiersEditor';

const offerTypes = [
  { value: 'flat_discount', label: 'Flat Discount (₹)', icon: Tag, color: 'blue' },
  { value: 'percentage_discount', label: 'Percentage Discount (%)', icon: Percent, color: 'emerald' },
  { value: 'buy_x_get_y', label: 'Buy X Get Y Free', icon: Gift, color: 'purple' },
  { value: 'qty_slab', label: 'Bulk Qty Price (by payment mode)', icon: Layers, color: 'indigo' },
  { value: 'coupon', label: 'Coupon Code', icon: Ticket, color: 'amber' }
];

const applicabilityOptions = [
  { value: 'all_products', label: 'All Products' },
  { value: 'specific_products', label: 'Specific Products' },
  { value: 'specific_categories', label: 'Specific Categories' },
  { value: 'specific_brands', label: 'Specific Brands' }
];

const initialFormData = {
  code: '',
  name: '',
  description: '',
  offer_type: 'percentage_discount',
  applicability: 'all_products',
  discount_value: 0,
  max_discount: null,
  min_purchase_amount: 0,
  min_purchase_qty: 0,
  buy_qty: 0,
  get_qty: 0,
  start_date: '',
  end_date: '',
  coupon_code: '',
  max_usage: null,
  max_usage_per_customer: 1,
  all_stores: true,
  applicable_store_ids: [],
  applicable_product_ids: [],
  applicable_category_ids: [],
  applicable_brand_ids: [],
  priority: 0,
  is_combinable: false,
  tiers: [emptyTier()]
};

const OffersPage = () => {
  const [offers, setOffers] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const { toast } = useToast();

  const fetchOffers = useCallback(async () => {
    try {
      const response = await offersAPI.list({ search });
      setOffers(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch offers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, toast]);

  const fetchMasterData = async () => {
    try {
      const [storeRes, categoryRes, brandRes] = await Promise.all([
        storesAPI.list({ is_active: true }),
        categoriesAPI.list({ is_active: true }),
        brandsAPI.list({ is_active: true })
      ]);
      setStores(storeRes.data);
      setCategories(categoryRes.data);
      setBrands(brandRes.data);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
    }
  };

  useEffect(() => { fetchOffers(); }, [fetchOffers]);
  useEffect(() => { fetchMasterData(); }, []);

  const handleOpenDialog = (offer = null) => {
    if (offer) {
      setSelectedOffer(offer);
      setFormData({
        code: offer.code,
        name: offer.name,
        description: offer.description || '',
        offer_type: offer.offer_type,
        applicability: offer.applicability,
        discount_value: offer.discount_value || 0,
        max_discount: offer.max_discount,
        min_purchase_amount: offer.min_purchase_amount || 0,
        min_purchase_qty: offer.min_purchase_qty || 0,
        buy_qty: offer.buy_qty || 0,
        get_qty: offer.get_qty || 0,
        start_date: formatDateForInput(offer.start_date),
        end_date: formatDateForInput(offer.end_date),
        coupon_code: offer.coupon_code || '',
        max_usage: offer.max_usage,
        max_usage_per_customer: offer.max_usage_per_customer || 1,
        all_stores: !offer.applicable_store_ids || offer.applicable_store_ids.length === 0,
        applicable_store_ids: offer.applicable_store_ids || [],
        applicable_product_ids: offer.applicable_product_ids || [],
        applicable_category_ids: offer.applicable_category_ids || [],
        applicable_brand_ids: offer.applicable_brand_ids || [],
        priority: offer.priority || 0,
        is_combinable: offer.is_combinable || false,
        tiers: offer.tiers?.length ? offer.tiers.map(t => ({ ...t, payment_modes: t.payment_modes || {} })) : [emptyTier()]
      });
    } else {
      setSelectedOffer(null);
      const today = new Date().toISOString().split('T')[0];
      const nextMonth = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      setFormData({ ...initialFormData, start_date: today, end_date: nextMonth });
    }
    setActiveTab('basic');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name || !formData.start_date || !formData.end_date) {
      toast({ title: 'Validation Error', description: 'Code, Name, Start Date, and End Date are required', variant: 'destructive' });
      return;
    }

    if (formData.offer_type === 'coupon' && !formData.coupon_code) {
      toast({ title: 'Validation Error', description: 'Coupon code is required for coupon offers', variant: 'destructive' });
      return;
    }

    if (formData.offer_type === 'qty_slab') {
      const bad = formData.tiers.find(t => !(Number(t.min_qty) > 0) || !(Number(t.value) > 0));
      const qtys = formData.tiers.map(t => Number(t.min_qty));
      if (!formData.tiers.length || bad) {
        toast({ title: 'Validation Error', description: 'Every slab needs a Min Qty and a Default value greater than 0', variant: 'destructive' });
        return;
      }
      if (new Set(qtys).size !== qtys.length) {
        toast({ title: 'Validation Error', description: 'Two slabs have the same Min Qty', variant: 'destructive' });
        return;
      }
    }

    // Validate applicability selections
    if (formData.applicability === 'specific_products' && formData.applicable_product_ids.length === 0) {
      toast({ title: 'Validation Error', description: 'Please select at least one product', variant: 'destructive' });
      return;
    }
    if (formData.applicability === 'specific_categories' && formData.applicable_category_ids.length === 0) {
      toast({ title: 'Validation Error', description: 'Please select at least one category', variant: 'destructive' });
      return;
    }
    if (formData.applicability === 'specific_brands' && formData.applicable_brand_ids.length === 0) {
      toast({ title: 'Validation Error', description: 'Please select at least one brand', variant: 'destructive' });
      return;
    }

    // Validate store selection
    if (!formData.all_stores && formData.applicable_store_ids.length === 0) {
      toast({ title: 'Validation Error', description: 'Please select at least one store', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        start_date: new Date(formData.start_date + 'T00:00:00+05:30').toISOString(),
        end_date: new Date(formData.end_date + 'T23:59:59+05:30').toISOString(),
        max_discount: formData.max_discount || null,
        max_usage: formData.max_usage || null,
        applicable_store_ids: formData.all_stores ? [] : formData.applicable_store_ids,
        tiers: formData.offer_type === 'qty_slab' ? formData.tiers : []
      };

      // Clear irrelevant selections based on applicability
      if (formData.applicability !== 'specific_products') payload.applicable_product_ids = [];
      if (formData.applicability !== 'specific_categories') payload.applicable_category_ids = [];
      if (formData.applicability !== 'specific_brands') payload.applicable_brand_ids = [];

      if (selectedOffer) {
        await offersAPI.update(selectedOffer.id, payload);
        toast({ title: 'Success', description: 'Offer updated successfully' });
      } else {
        await offersAPI.create(payload);
        toast({ title: 'Success', description: 'Offer created successfully' });
      }
      setDialogOpen(false);
      fetchOffers();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save offer', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await offersAPI.delete(selectedOffer.id);
      toast({ title: 'Success', description: 'Offer deactivated successfully' });
      setDeleteDialogOpen(false);
      fetchOffers();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete offer', variant: 'destructive' });
    }
  };

  const toggleStore = (storeId) => {
    setFormData(prev => ({
      ...prev,
      applicable_store_ids: prev.applicable_store_ids.includes(storeId)
        ? prev.applicable_store_ids.filter(id => id !== storeId)
        : [...prev.applicable_store_ids, storeId]
    }));
  };

  const getOfferTypeInfo = (type) => offerTypes.find(t => t.value === type) || offerTypes[0];

  const isOfferActive = (offer) => {
    const now = new Date();
    const start = new Date(offer.start_date);
    const end = new Date(offer.end_date);
    return offer.is_active && now >= start && now <= end;
  };

  const formatDiscount = (offer) => {
    if (offer.offer_type === 'flat_discount') return `₹${offer.discount_value} OFF`;
    if (offer.offer_type === 'percentage_discount') return `${offer.discount_value}% OFF`;
    if (offer.offer_type === 'buy_x_get_y') return `Buy ${offer.buy_qty} Get ${offer.get_qty} Free`;
    if (offer.offer_type === 'qty_slab') {
      const tiers = [...(offer.tiers || [])].sort((a, b) => Number(a.min_qty) - Number(b.min_qty));
      return tiers.length ? `${tiers.length} bulk slab${tiers.length > 1 ? 's' : ''} from ${Number(tiers[0].min_qty)} pcs` : 'Bulk price';
    }
    if (offer.offer_type === 'coupon') return offer.discount_value > 0 ? `${offer.discount_value}%` : 'Coupon';
    return '';
  };

  // Prepare items for SearchableMultiSelect (category & brand pickers only — products use ServerProductMultiSelect)
  const categoryItems = categories.map(c => ({ 
    id: c.id, 
    name: c.level > 1 ? `${'—'.repeat(c.level - 1)} ${c.name}` : c.name, 
    code: c.code 
  }));
  const brandItems = brands.map(b => ({ id: b.id, name: b.name, code: b.code }));

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Offers & Promotions</h1>
          <p className="text-gray-500">Manage discounts, coupons, and promotional offers</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-pink-500 hover:bg-pink-600 text-white shadow-lg shadow-pink-200" data-testid="add-offer-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Offer
        </Button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search offers..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-300 text-gray-800 focus:border-pink-500" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : offers.length === 0 ? (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="py-12 text-center">
            <Gift className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No offers found</p>
            <Button onClick={() => handleOpenDialog()} variant="link" className="text-pink-600 mt-2">
              Create your first offer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {offers.map(offer => {
            const typeInfo = getOfferTypeInfo(offer.offer_type);
            const TypeIcon = typeInfo.icon;
            const active = isOfferActive(offer);
            
            return (
              <Card key={offer.id} className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`offer-card-${offer.code}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-${typeInfo.color}-100 flex items-center justify-center`}>
                        <TypeIcon className={`w-5 h-5 text-${typeInfo.color}-600`} />
                      </div>
                      <div>
                        <CardTitle className="text-gray-800 text-lg">{offer.name}</CardTitle>
                        <p className="text-sm text-gray-500">{offer.code}</p>
                      </div>
                    </div>
                    <Badge className={active ? 'bg-emerald-100 text-emerald-700' : offer.is_active ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}>
                      {active ? 'Active' : offer.is_active ? 'Scheduled' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 text-center border border-gray-200">
                    <p className="text-2xl font-bold text-gray-800">{formatDiscount(offer)}</p>
                    {offer.min_purchase_amount > 0 && (
                      <p className="text-xs text-gray-500">Min. purchase ₹{offer.min_purchase_amount}</p>
                    )}
                    {offer.offer_type === 'qty_slab' && (
                      <div className="mt-2 space-y-0.5 text-left" data-testid={`offer-card-tiers-${offer.code}`}>
                        {[...(offer.tiers || [])].sort((a, b) => Number(a.min_qty) - Number(b.min_qty)).map((t, i) => (
                          <p key={i} className="text-xs text-indigo-700">{formatTier(t)}</p>
                        ))}
                      </div>
                    )}
                  </div>

                  {offer.coupon_code && (
                    <div className="flex items-center gap-2 p-2 rounded bg-amber-50 border border-dashed border-amber-300">
                      <Ticket className="w-4 h-4 text-amber-600" />
                      <span className="font-mono text-amber-700">{offer.coupon_code}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      {formatDateOnly(offer.start_date)} - {formatDateOnly(offer.end_date)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Store className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      {!offer.applicable_store_ids || offer.applicable_store_ids.length === 0 
                        ? 'All Stores' 
                        : `${offer.applicable_store_ids.length} Store(s)`}
                    </span>
                  </div>

                  {offer.applicability !== 'all_products' && (
                    <div className="text-xs text-gray-500">
                      {offer.applicability === 'specific_products' && `${offer.applicable_product_ids?.length || 0} products`}
                      {offer.applicability === 'specific_categories' && `${offer.applicable_category_ids?.length || 0} categories`}
                      {offer.applicability === 'specific_brands' && `${offer.applicable_brand_ids?.length || 0} brands`}
                    </div>
                  )}

                  <div className="flex gap-2 pt-3 border-t border-gray-200">
                    <Button variant="outline" size="sm" className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-50"
                      onClick={() => handleOpenDialog(offer)} data-testid={`offer-edit-${offer.code}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => { setSelectedOffer(offer); setDeleteDialogOpen(true); }} data-testid={`offer-delete-${offer.code}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">{selectedOffer ? 'Edit Offer' : 'Create Offer'}</DialogTitle>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-gray-100 w-full justify-start">
              <TabsTrigger value="basic" className="data-[state=active]:bg-white">Basic Info</TabsTrigger>
              <TabsTrigger value="stores" className="data-[state=active]:bg-white">Stores</TabsTrigger>
              <TabsTrigger value="products" className="data-[state=active]:bg-white">Products/Categories</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Offer Code *</Label>
                  <Input value={formData.code} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., SUMMER2025" className="bg-gray-50 border-gray-300" disabled={!!selectedOffer} data-testid="offer-code-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Offer Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Summer Sale 20% Off" className="bg-gray-50 border-gray-300" data-testid="offer-name-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Offer description" className="bg-gray-50 border-gray-300" rows={2} />
              </div>

              {/* Offer Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Offer Type</Label>
                  <Select value={formData.offer_type} onValueChange={(v) => setFormData(p => ({ ...p, offer_type: v }))}>
                    <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="offer-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {offerTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Applies To</Label>
                  <Select value={formData.applicability} onValueChange={(v) => setFormData(p => ({ ...p, applicability: v }))}>
                    <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="offer-applicability-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {applicabilityOptions.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Discount Values */}
              {(formData.offer_type === 'flat_discount' || formData.offer_type === 'percentage_discount' || formData.offer_type === 'coupon') && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">
                      {formData.offer_type === 'flat_discount' ? 'Discount Amount (₹)' : 'Discount (%)'}
                    </Label>
                    <Input type="number" value={formData.discount_value}
                      onChange={(e) => setFormData(p => ({ ...p, discount_value: parseFloat(e.target.value) || 0 }))}
                      className="bg-gray-50 border-gray-300" />
                    {(formData.offer_type === 'percentage_discount' || formData.offer_type === 'flat_discount') && (
                      <p className="text-xs text-gray-500">
                        Applied on <b>MRP</b>. Final sell Rate is capped at the product's Selling Price (SP) so a weak offer never prices above SP.
                      </p>
                    )}
                  </div>
                  {formData.offer_type !== 'flat_discount' && (
                    <div className="space-y-2">
                      <Label className="text-gray-700">Max Discount (₹)</Label>
                      <Input type="number" value={formData.max_discount || ''}
                        onChange={(e) => setFormData(p => ({ ...p, max_discount: e.target.value ? parseFloat(e.target.value) : null }))}
                        placeholder="No limit" className="bg-gray-50 border-gray-300" />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-gray-700">Min. Purchase (₹)</Label>
                    <Input type="number" value={formData.min_purchase_amount}
                      onChange={(e) => setFormData(p => ({ ...p, min_purchase_amount: parseFloat(e.target.value) || 0 }))}
                      className="bg-gray-50 border-gray-300" />
                  </div>
                </div>
              )}

              {/* Buy X Get Y */}
              {formData.offer_type === 'buy_x_get_y' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Buy Quantity</Label>
                    <Input type="number" value={formData.buy_qty}
                      onChange={(e) => setFormData(p => ({ ...p, buy_qty: parseInt(e.target.value) || 0 }))}
                      className="bg-gray-50 border-gray-300" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Get Free Quantity</Label>
                    <Input type="number" value={formData.get_qty}
                      onChange={(e) => setFormData(p => ({ ...p, get_qty: parseInt(e.target.value) || 0 }))}
                      className="bg-gray-50 border-gray-300" />
                  </div>
                </div>
              )}

              {/* Qty slab tiers */}
              {formData.offer_type === 'qty_slab' && (
                <OfferTiersEditor tiers={formData.tiers} onChange={(tiers) => setFormData(p => ({ ...p, tiers }))} />
              )}

              {/* Coupon specific */}
              {formData.offer_type === 'coupon' && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Coupon Code *</Label>
                    <Input value={formData.coupon_code} onChange={(e) => setFormData(p => ({ ...p, coupon_code: e.target.value.toUpperCase() }))}
                      placeholder="e.g., SAVE20" className="bg-gray-50 border-gray-300 font-mono" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Total Usage Limit</Label>
                    <Input type="number" value={formData.max_usage || ''}
                      onChange={(e) => setFormData(p => ({ ...p, max_usage: e.target.value ? parseInt(e.target.value) : null }))}
                      placeholder="Unlimited" className="bg-gray-50 border-gray-300" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Per Customer Limit</Label>
                    <Input type="number" value={formData.max_usage_per_customer}
                      onChange={(e) => setFormData(p => ({ ...p, max_usage_per_customer: parseInt(e.target.value) || 1 }))}
                      className="bg-gray-50 border-gray-300" />
                  </div>
                </div>
              )}

              {/* Validity */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Start Date *</Label>
                  <Input type="date" value={formData.start_date}
                    onChange={(e) => setFormData(p => ({ ...p, start_date: e.target.value }))}
                    className="bg-gray-50 border-gray-300" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">End Date *</Label>
                  <Input type="date" value={formData.end_date}
                    onChange={(e) => setFormData(p => ({ ...p, end_date: e.target.value }))}
                    className="bg-gray-50 border-gray-300" />
                </div>
              </div>

              {/* Combinable */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-200">
                <div>
                  <p className="text-gray-800 font-medium">Combinable with other offers</p>
                  <p className="text-sm text-gray-500">Allow this offer to stack with other promotions</p>
                </div>
                <Switch checked={formData.is_combinable} onCheckedChange={(v) => setFormData(p => ({ ...p, is_combinable: v }))} />
              </div>
            </TabsContent>

            <TabsContent value="stores" className="space-y-4 mt-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-50 border border-emerald-100">
                <div>
                  <p className="text-gray-800 font-medium">Apply to All Stores</p>
                  <p className="text-sm text-gray-500">This offer will be valid at all store locations</p>
                </div>
                <Switch 
                  checked={formData.all_stores} 
                  onCheckedChange={(v) => setFormData(p => ({ ...p, all_stores: v, applicable_store_ids: v ? [] : p.applicable_store_ids }))} 
                />
              </div>

              {!formData.all_stores && (
                <div className="space-y-2">
                  <Label className="text-gray-700 flex items-center gap-2">
                    <Store className="w-4 h-4" />
                    Select Specific Stores
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">Select the stores where this offer will be valid</p>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-2 max-h-64 overflow-y-auto">
                    {stores.length === 0 ? (
                      <p className="text-sm text-gray-500">No stores available</p>
                    ) : (
                      stores.map(store => (
                        <div
                          key={store.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            formData.applicable_store_ids.includes(store.id)
                              ? 'bg-emerald-100 border-emerald-300'
                              : 'bg-white border-gray-200 hover:border-emerald-200'
                          }`}
                          onClick={() => toggleStore(store.id)}
                        >
                          <Checkbox
                            checked={formData.applicable_store_ids.includes(store.id)}
                            onCheckedChange={() => toggleStore(store.id)}
                            className="pointer-events-none"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{store.name}</p>
                            <p className="text-xs text-gray-500">{store.code} • {store.address?.city || store.address?.district}</p>
                          </div>
                          {formData.applicable_store_ids.includes(store.id) && (
                            <Check className="w-4 h-4 text-emerald-600" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  {formData.applicable_store_ids.length > 0 && (
                    <p className="text-xs text-emerald-600">{formData.applicable_store_ids.length} store(s) selected</p>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="products" className="space-y-4 mt-4">
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                <p className="text-sm text-blue-700">
                  Current selection: <strong>{applicabilityOptions.find(a => a.value === formData.applicability)?.label}</strong>
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Change the "Applies To" option in Basic Info tab to select different targets
                </p>
              </div>

              {formData.applicability === 'all_products' && (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>This offer applies to all products</p>
                </div>
              )}

              {formData.applicability === 'specific_products' && (
                <div className="space-y-2">
                  <Label className="text-gray-700 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Select Products
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">Filter by categories or brands, then search by name/SKU. Use "Select all shown" to bulk-add.</p>
                  <ServerProductMultiSelect
                    selectedIds={formData.applicable_product_ids}
                    onChange={(ids) => setFormData(p => ({ ...p, applicable_product_ids: ids }))}
                    placeholder="Select products…"
                    searchPlaceholder="Search by name or SKU…"
                    emptyMessage="No products found — try a different filter or search"
                  />
                </div>
              )}

              {formData.applicability === 'specific_categories' && (
                <div className="space-y-2">
                  <Label className="text-gray-700 flex items-center gap-2">
                    <FolderTree className="w-4 h-4" />
                    Select Categories
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">Search and select categories for this offer</p>
                  <SearchableMultiSelect
                    items={categoryItems}
                    selectedIds={formData.applicable_category_ids}
                    onChange={(ids) => setFormData(p => ({ ...p, applicable_category_ids: ids }))}
                    placeholder="Select categories..."
                    searchPlaceholder="Search by name or code..."
                    emptyMessage="No categories found"
                    color="blue"
                    maxDisplay={8}
                  />
                </div>
              )}

              {formData.applicability === 'specific_brands' && (
                <div className="space-y-2">
                  <Label className="text-gray-700 flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Select Brands
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">Search and select brands for this offer</p>
                  <SearchableMultiSelect
                    items={brandItems}
                    selectedIds={formData.applicable_brand_ids}
                    onChange={(ids) => setFormData(p => ({ ...p, applicable_brand_ids: ids }))}
                    placeholder="Select brands..."
                    searchPlaceholder="Search by name or code..."
                    emptyMessage="No brands found"
                    color="purple"
                    maxDisplay={8}
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300 text-gray-600">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="offer-save-btn">
              {saving ? 'Saving...' : 'Save Offer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Deactivate Offer?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will deactivate "{selectedOffer?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OffersPage;
